/**
 * Test coverage visualization across 4 layers: unit, integration, E2E, golden.
 * Shows layer bars and provides a "Generate Tests" action.
 */

import { useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FlaskConical, Loader2 } from 'lucide-react';
import { generateOutcomeTests } from '@/lib/api';

interface TestLayerData {
  layer: string;
  passed: number;
  total: number;
}

interface TestLayerCardProps {
  codebaseId: string;
  outcomeId: string;
  /** Whether a test-outcome-plan.md artifact exists */
  hasTestPlan: boolean;
  /** Actual test results per layer — when absent, shows "No test data" */
  testData?: TestLayerData[];
}

// Test layers are a categorical series — four kinds of test, not four states — so
// they use the --category-* scale rather than status tokens.
const LAYERS = [
  { key: 'unit', label: 'Unit', color: 'bg-category-1' },
  { key: 'integration', label: 'Integration', color: 'bg-category-2' },
  { key: 'e2e', label: 'E2E', color: 'bg-category-3' },
  { key: 'golden', label: 'Golden', color: 'bg-category-4' },
] as const;

export function TestLayerCard({
  codebaseId,
  outcomeId,
  hasTestPlan,
  testData,
}: TestLayerCardProps): React.ReactElement {
  const navigate = useNavigate();

  const generateMutation = useMutation({
    mutationFn: () => generateOutcomeTests(codebaseId, outcomeId),
    onSuccess: result => {
      void navigate(`/chat/${result.conversationId}`);
    },
  });

  return (
    <Card className="border-border/70 bg-surface/95 shadow-sm">
      <CardHeader className="px-4 py-4">
        <CardTitle className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-primary" />
            <span>Test Coverage</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-4">
        {hasTestPlan ? (
          <>
            <div className="space-y-2 rounded-xl border border-border/60 bg-background/70 px-3 py-3">
              {LAYERS.map(layer => {
                const data = testData?.find(d => d.layer === layer.key);
                const pct =
                  data && data.total > 0 ? Math.round((data.passed / data.total) * 100) : 0;
                const hasData = data !== undefined && data.total > 0;
                return (
                  <div key={layer.key} className="flex items-center gap-2">
                    <span className="text-xs text-text-secondary w-20">{layer.label}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-elevated">
                      <div
                        className={`h-full rounded-full ${layer.color} opacity-70`}
                        style={{ width: hasData ? `${String(pct)}%` : '0%' }}
                      />
                    </div>
                    {hasData && (
                      <span className="text-[10px] text-text-tertiary w-12 text-right">
                        {data.passed}/{data.total}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-success/20 bg-success/5 px-3 py-2 text-xs text-text-tertiary">
              <div className="h-2 w-2 rounded-full bg-success" />
              <span>Test plan generated</span>
            </div>
          </>
        ) : (
          <p className="rounded-xl border border-border/60 bg-background/70 px-3 py-3 text-xs leading-5 text-text-secondary">
            No test plan yet. Generate tests from this outcome's acceptance criteria across unit,
            integration, E2E, and golden (AI agent) layers.
          </p>
        )}

        <Button
          size="sm"
          variant={hasTestPlan ? 'outline' : 'default'}
          onClick={() => {
            generateMutation.mutate();
          }}
          disabled={generateMutation.isPending}
          className="h-9 w-full rounded-xl text-xs"
        >
          {generateMutation.isPending ? (
            <>
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              Starting...
            </>
          ) : (
            <>
              <FlaskConical className="mr-1.5 h-3 w-3" />
              {hasTestPlan ? 'Regenerate Tests' : 'Generate Tests'}
            </>
          )}
        </Button>

        {generateMutation.isError && (
          <div className="text-xs text-error">
            {generateMutation.error instanceof Error
              ? generateMutation.error.message
              : 'Failed to start test generation'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
